import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateSettings: (data) => api.put('/auth/settings', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
};

// Events APIs
export const eventsAPI = {
  getAll: (params) => api.get('/events', { params }),
  getOne: (id) => api.get(`/events/${id}`),
  getMyCreated: () => api.get('/events/user/created'),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  importFromUrl: (url) => api.post('/events/import-url', { url }),
  // New duplicate-aware import endpoints
  previewImport: (url) => api.post('/events/import-url/preview', { url }),
  confirmImport: (events, decisions, saveToCommunity = false) => api.post('/events/import-url/confirm', { 
    events, 
    decisions,
    save_to_community: saveToCommunity 
  }),
  mergeEvent: (targetSystemId, newData) => api.post('/events/merge', { target_system_id: targetSystemId, new_data: newData }),
  refreshThumbnail: (id) => api.post(`/events/${id}/refresh-thumbnail`),
  refreshAllThumbnails: () => api.post('/events/refresh-all-thumbnails'),
  // Community event cleanup
  getExpiredCommunityCount: () => api.get('/events/community/expired/count'),
  deleteExpiredCommunity: () => api.delete('/events/community/expired'),
  // Series linking
  linkToSeries: (eventId, seriesId) => api.post(`/events/${eventId}/link-to-series`, { series_id: seriesId }),
  unlinkFromSeries: (eventId) => api.delete(`/events/${eventId}/unlink-from-series`),
  suggestSeries: (eventId) => api.get(`/events/${eventId}/suggest-series`),
  getSeriesHistory: (eventId) => api.get(`/events/${eventId}/series-history`),
};

// Event Series API
export const eventSeriesAPI = {
  getAll: () => api.get('/event-series'),
  get: (id) => api.get(`/event-series/${id}`),
  create: (data) => api.post('/event-series', data),
  update: (id, data) => api.put(`/event-series/${id}`, data),
  delete: (id) => api.delete(`/event-series/${id}`),
};

// Favorites APIs
export const favoritesAPI = {
  getAll: () => api.get('/favorites'),
  add: (eventId) => api.post(`/favorites/${eventId}`),
  remove: (eventId) => api.delete(`/favorites/${eventId}`),
  savePreview: (eventData) => api.post('/favorites/save-preview', eventData),
};

// AI Discovery API
export const aiAPI = {
  discover: (data) => api.post('/ai/discover', data),
};

// Hybrid Event Scout API (New)
export const eventScoutAPI = {
  search: (data) => api.post('/event-scout/search', data),
  getTrustSources: () => api.get('/event-scout/trust-sources'),
};

// Saved Searches API
export const savedSearchesAPI = {
  getAll: () => api.get('/saved-searches'),
  create: (data) => api.post('/saved-searches', data),
  use: (id) => api.put(`/saved-searches/${id}/use`),
  delete: (id) => api.delete(`/saved-searches/${id}`),
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
  getActionCenter: () => api.get('/dashboard/action-center'),
};

// URL Validation API
export const validateAPI = {
  single: (url) => api.get('/validate-url', { params: { url } }),
  batch: (urls) => api.post('/validate-urls', { urls }),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Geocoding API
export const geocodeAPI = {
  getCoords: (city, state, address, zip) => {
    // Build the most complete address possible
    const parts = [];
    if (address) parts.push(address);
    if (city) parts.push(city);
    if (state) parts.push(state);
    if (zip) parts.push(zip);
    const fullAddress = parts.join(', ');
    return api.get('/geocode', { params: { address: fullAddress } });
  },
  batch: (locations) => {
    // Build full addresses for each location, filtering out empty ones
    const addresses = locations
      .map(loc => {
        const parts = [];
        if (loc.location) parts.push(loc.location);
        if (loc.city) parts.push(loc.city);
        if (loc.state) parts.push(loc.state);
        if (loc.zip) parts.push(loc.zip);
        return parts.join(', ');
      })
      .filter(addr => addr.trim().length > 0);  // Filter out empty addresses
    
    if (addresses.length === 0) {
      // Return empty result immediately if no valid addresses
      return Promise.resolve({ data: [] });
    }
    return api.post('/geocode/batch', addresses);
  },
  // Legacy method for backward compatibility
  simple: (city, state) => api.get('/geocode', { params: { address: `${city}, ${state}` } }),
  reverse: (lat, lon) => api.get('/geocode/reverse', { params: { lat, lon } }),
};

// Materials API
export const materialsAPI = {
  getAll: (params) => api.get('/materials', { params }),
  getOne: (id) => api.get(`/materials/${id}`),
  create: (data) => api.post('/materials', data),
  update: (id, data) => api.put(`/materials/${id}`, data),
  delete: (id) => api.delete(`/materials/${id}`),
};

// User Settings API (categories, venue types, etc.)
export const userSettingsAPI = {
  get: (settingType) => api.get(`/user-settings/${settingType}`),
  update: (settingType, values) => api.post(`/user-settings/${settingType}`, { values }),
  addValue: (settingType, value) => api.post(`/user-settings/${settingType}/add`, { value }),
  removeValue: (settingType, value) => api.post(`/user-settings/${settingType}/remove`, { value }),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getPricing: (data) => api.post('/products/ai-pricing', data),
  getCategories: () => api.get('/product-categories'),
};

// Application Profiles API
export const profilesAPI = {
  getAll: () => api.get('/profiles'),
  getOne: (id) => api.get(`/profiles/${id}`),
  create: (data) => api.post('/profiles', data),
  update: (id, data) => api.put(`/profiles/${id}`, data),
  delete: (id) => api.delete(`/profiles/${id}`),
};

// Media Library API
export const mediaAPI = {
  getAll: (params) => api.get('/media', { params }),
  upload: (file, mediaType, tags, description) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', mediaType);
    formData.append('tags', tags || '');
    formData.append('description', description || '');
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => api.delete(`/media/${id}`),
};

// Applications API
export const applicationsAPI = {
  getAll: (params) => api.get('/applications', { params }),
  getOne: (id) => api.get(`/applications/${id}`),
  getByEvent: (eventId) => api.get(`/applications/by-event/${eventId}`),
  getByEvents: (eventIds) => api.post('/applications/by-events', eventIds),
  deadlineReminders: (windowDays = 7) =>
    api.get('/applications/deadline-reminders', { params: { window_days: windowDays } }),
  create: (data) => api.post('/applications', data),
  update: (id, data) => api.put(`/applications/${id}`, data),
  delete: (id) => api.delete(`/applications/${id}`),
  copy: (data) => api.post('/applications/copy', data),
  email: (data) => api.post('/applications/email', data),
};

// Tasks API
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  reorder: (taskOrders) => api.post('/tasks/reorder', taskOrders),
  getUpcoming: (days = 7) => api.get('/tasks/upcoming', { params: { days } }),
  getTemplates: () => api.get('/task-templates'),
  createTemplate: (data) => api.post('/task-templates', data),
  deleteTemplate: (id) => api.delete(`/task-templates/${id}`),
};

// Sales API
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  getOne: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  delete: (id) => api.delete(`/sales/${id}`),
  getChannels: () => api.get('/sales/channels'),
  getChannelOptions: () => api.get('/sales/channel-options'),
};

// Custom Channels API
export const customChannelsAPI = {
  getAll: () => api.get('/custom-channels'),
  getOne: (id) => api.get(`/custom-channels/${id}`),
  create: (data) => api.post('/custom-channels', data),
  update: (id, data) => api.put(`/custom-channels/${id}`, data),
  delete: (id) => api.delete(`/custom-channels/${id}`),
};

// Boutiques API
export const boutiquesAPI = {
  getAll: (params) => api.get('/boutiques', { params }),
  getOne: (id) => api.get(`/boutiques/${id}`),
  create: (data) => api.post('/boutiques', data),
  update: (id, data) => api.put(`/boutiques/${id}`, data),
  delete: (id) => api.delete(`/boutiques/${id}`),
};

// Consignments API
export const consignmentsAPI = {
  getAll: (params) => api.get('/consignments', { params }),
  create: (data) => api.post('/consignments', data),
  update: (id, data) => api.put(`/consignments/${id}`, data),
  returnItems: (id, quantity) => api.post(`/consignments/${id}/return`, null, { params: { quantity } }),
};

// Boutique Payments API
export const boutiquePaymentsAPI = {
  getAll: (params) => api.get('/boutique-payments', { params }),
  create: (data) => api.post('/boutique-payments', data),
  delete: (id) => api.delete(`/boutique-payments/${id}`),
};

// Inventory API
export const inventoryAPI = {
  getAll: () => api.get('/inventory'),
  getOne: (productId) => api.get(`/inventory/${productId}`),
  update: (productId, data) => api.put(`/inventory/${productId}`, data),
  allocate: (productId, data) => api.post(`/inventory/${productId}/allocate`, data),
  removeAllocation: (productId, allocationId) => api.delete(`/inventory/${productId}/allocations/${allocationId}`),
  getLowStockAlerts: () => api.get('/inventory/alerts/low-stock'),
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getOne: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Event Financials API
export const financialsAPI = {
  getEventFinancials: (eventId) => api.get(`/events/${eventId}/financials`),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  whatIf: (eventIds) => api.post('/analytics/what-if', { event_ids: eventIds }),
  getSeriesPerformance: () => api.get('/analytics/series-performance'),
};

// User Settings API
export const settingsAPI = {
  updateSettings: (data) => api.put('/users/settings', null, { params: data }),
};

// Bulk Import API
export const importAPI = {
  importEvents: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/events', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importProducts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importMaterials: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/materials', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importSales: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/sales', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importBoutiques: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/boutiques', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getTemplate: (entityType) => api.get(`/import/templates/${entityType}`),
};

// Etsy Integration API
export const etsyAPI = {
  getAuthUrl: () => api.get('/etsy/auth-url'),
  getStatus: () => api.get('/etsy/status'),
  disconnect: () => api.post('/etsy/disconnect'),
  sync: () => api.post('/etsy/sync'),
  getOrders: (params) => api.get('/etsy/orders', { params }),
  importToSales: (orderId) => api.post(`/etsy/import-to-sales/${orderId}`),
};

// Subscription API
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  getPricing: () => api.get('/subscription/pricing'),
  createCheckout: (originUrl, plan = 'monthly') => api.post('/subscription/checkout', { origin_url: originUrl, plan }),
  getCheckoutStatus: (sessionId) => api.get(`/subscription/checkout/status/${sessionId}`),
  cancel: () => api.post('/subscription/cancel'),
  openPortal: (returnUrl) => api.post('/subscription/portal', { return_url: returnUrl }),
};

// Cloud Storage API
export const cloudStorageAPI = {
  // Connection status
  getStatus: () => api.get('/cloud-storage/status'),
  
  // Google Drive OAuth
  connectGoogleDrive: () => api.get('/cloud-storage/oauth/google/connect'),
  disconnectGoogleDrive: () => api.delete('/cloud-storage/oauth/google/disconnect'),
  
  // Google Drive file browsing
  listGoogleDriveFiles: (folderId = 'root', pageToken = null) => 
    api.get('/cloud-storage/google-drive/files', { 
      params: { folder_id: folderId, page_token: pageToken } 
    }),
  
  // Import files
  importFromGoogleDrive: (files) => api.post('/cloud-storage/import/google-drive', {
    files,
    source: 'google_drive'
  }),
  importFromDropbox: (files) => api.post('/cloud-storage/import/dropbox', {
    files,
    source: 'dropbox'
  }),
};

// Admin API (requires admin role)
export const adminAPI = {
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // App Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // User Management
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  // Subscription Management
  resetTrial: (userId, trialDays = 7) => api.post(`/admin/users/${userId}/reset-trial`, { trial_days: trialDays }),
  grantLifetime: (userId) => api.post(`/admin/users/${userId}/grant-lifetime`),
  revokeAccess: (userId) => api.post(`/admin/users/${userId}/revoke-access`),
  activateSubscription: (userId) => api.post(`/admin/users/${userId}/activate-subscription`),
};

// ==================== CONFIG (Shared registries) ====================
export const configAPI = {
  eventTypes: () => api.get('/config/event-types'),
};

// ==================== VENDOR COMMUNITY (Phase A + B) ====================
export const communityAPI = {
  stats: () => api.get('/community/stats'),
  listVendors: (params = {}) => api.get('/community/vendors', { params }),
  getVendor: (slug) => api.get(`/community/vendors/${slug}`),
  getVendorEvents: (slug) => api.get(`/community/vendors/${slug}/events`),
  follow: (userId) => api.post(`/community/follow/${userId}`),
  unfollow: (userId) => api.delete(`/community/follow/${userId}`),
  followStatus: (userId) => api.get(`/community/follow/status/${userId}`),
  listFollowers: (userId) => api.get(`/community/follow/${userId}/followers`),
  listFollowing: (userId) => api.get(`/community/follow/${userId}/following`),
  followingFeedEvents: () => api.get('/community/me/following/events'),
};

// ==================== EVENT REVIEWS (community events only) ====================
export const eventReviewsAPI = {
  list: (eventId) => api.get(`/events/${eventId}/reviews`),
  upsert: (eventId, data) => api.post(`/events/${eventId}/reviews`, data),
  delete: (eventId, reviewId) => api.delete(`/events/${eventId}/reviews/${reviewId}`),
};

// ==================== EVENT UPDATE REQUESTS (moderation inbox) ====================
export const eventUpdateRequestsAPI = {
  submit: (eventId, payload) => api.post(`/events/${eventId}/update-requests`, payload),
  adminList: (status = 'pending') => api.get('/admin/event-update-requests', { params: { status } }),
  adminResolve: (requestId, payload) => api.patch(`/admin/event-update-requests/${requestId}`, payload),
};

export default api;
